// NOAA General Solar Position equations. All clock inputs are explicitly SGT;
// browser timezone never participates. Azimuth is clockwise from true north.
export const radians = Math.PI / 180;
export const normalizeDegrees = (angle: number) => ((angle % 360) + 360) % 360;

export function solarPosition(month: number, minutes: number, latitude: number, longitude: number, year = 2026, day = 15) {
  const date = Date.UTC(year, month, day);
  const dayOfYear = (date - Date.UTC(year, 0, 0)) / 86400000;
  const days = (Date.UTC(year + 1, 0, 1) - Date.UTC(year, 0, 1)) / 86400000;
  const gamma = 2 * Math.PI / days * (dayOfYear - 1 + (minutes / 60 - 12) / 24);
  const eq = 229.18 * (0.000075 + 0.001868 * Math.cos(gamma) - 0.032077 * Math.sin(gamma)
    - 0.014615 * Math.cos(2 * gamma) - 0.040849 * Math.sin(2 * gamma));
  const dec = 0.006918 - 0.399912 * Math.cos(gamma) + 0.070257 * Math.sin(gamma)
    - 0.006758 * Math.cos(2 * gamma) + 0.000907 * Math.sin(2 * gamma)
    - 0.002697 * Math.cos(3 * gamma) + 0.00148 * Math.sin(3 * gamma);
  const hourAngle = ( (minutes + eq + 4 * longitude - 480) / 4 - 180) * radians;
  const phi = latitude * radians;
  const elevation = Math.asin(Math.max(-1, Math.min(1,
    Math.sin(phi) * Math.sin(dec) + Math.cos(phi) * Math.cos(dec) * Math.cos(hourAngle)))) / radians;
  const azimuth = normalizeDegrees(Math.atan2(Math.sin(hourAngle),
    Math.cos(hourAngle) * Math.sin(phi) - Math.tan(dec) * Math.cos(phi)) / radians + 180);
  return { azimuth, elevation };
}

// Image right = +X, image bottom = +Z, height = +Y. Camera rotation is
// deliberately absent: sun, compass and buildings share this world space.
export function solarVector(azimuth: number, elevation: number, north: number) {
  const bearing = (azimuth + north) * radians;
  const altitude = elevation * radians;
  return { x: Math.sin(bearing) * Math.cos(altitude), y: Math.sin(altitude), z: -Math.cos(bearing) * Math.cos(altitude) };
}

export const solarTimeLabel = (minutes: number) => `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(Math.floor(minutes % 60)).padStart(2, '0')}`;
